import { isBrowser } from "@montana/helpers/browser.helper";

import {
    WindowWithDL,
    IGtmDataLayerEvent,
    IGtmGenericVariables,
    IGtmVirtualPageView,
    IGtmProductWithQuantity,
    IGtmCheckoutStep,
    IGtmCustomEvent,
    IGtmCheckoutComplete,
    IGtmProductView,
    IGtmProduct,
    IGtmProductImpression,
    IGtmProductFromList,
    IGtmProductClick,
    IGtmAddToCart,
    IGtmRemoveFromCart,
    IGtmCheckoutOption,
    GtmCheckoutStep,
    IGtmPurchase,
    IGtmPurchaseActionField
} from "./tracking";


function push(gtmDataLayerObj: IGtmDataLayerEvent | IGtmGenericVariables | { [key: string]: string }) {
    if (!isBrowser) {
        return;
    }

    (window as WindowWithDL).dataLayer = (window as WindowWithDL).dataLayer || [];
    (window as WindowWithDL).dataLayer.push(gtmDataLayerObj);
}


export function trackGtmStart(): void {
    push({
        "gtm.start": new Date().getTime(),
        event: "gtm.js"
    });
}

export function setupGenericVariables(obj: IGtmGenericVariables): void {
    push(obj);
}

export function trackVirtualPageView(obj: Omit<IGtmVirtualPageView, "event">): void {
    push({ event: "virtualPageView", ...obj });
}

/**
 * @param {GtmCheckoutStep} step ONE = basket, TWO = checkout panel, THREE = go to payment
 */
export function trackCheckoutStep(step: number, products: IGtmProductWithQuantity[], currencyCode: string): void {
    const event: IGtmCheckoutStep = {
        event: "eec_checkout",
        ecommerce: {
            currencyCode,
            checkout: { actionField: { step }, products }
        }
    };

    push(event);
}

export function trackCheckoutCompleted(id: string, revenue: number, shipping: number, products: IGtmProductWithQuantity[], currencyCode: string): void {
    const event: IGtmCheckoutComplete = {
        event: "eec_purchase",
        ecommerce: {
            currencyCode,
            checkout: {
                actionField: {
                    id,
                    revenue,
                    shipping,
                }, products
            }
        }
    };

    push(event);
}

export function trackCustomEvent(eventObject: { [key: string]: string }): void {
    push(eventObject);
}

export function trackCustomGAEvent(eventCategory: string, eventAction: string, eventLabel?: string): void {
    const event: IGtmCustomEvent = {
        event: "gaEvent",
        eventCategory,
        eventAction
    };

    if (eventLabel) {
        event.eventLabel = eventLabel;
    }

    push(event);
}

export function trackProductView(currencyCode: string, products: IGtmProduct[]): void {
    const event: IGtmProductView = {
        event: "eec_productView",
        ecommerce: {
            currencyCode,
            detail: {
                products,
            },
        },
    };

    push(event);
}

export function trackProductImpression(currencyCode: string, impressions: IGtmProductFromList[]): void {
    const event: IGtmProductImpression = {
        event: "eec_productImpression",
        ecommerce: {
            currencyCode,
            impressions,
        },
    };

    push(event);
}

export function trackProductClick(currencyCode: string, product: IGtmProductFromList, category: string): void {
    const event: IGtmProductClick = {
        event: "eec_productClick",
        ecommerce: {
            currencyCode,
            click: {
                actionField: {
                    list: category
                },
                products: [product],
            }
        },
    };

    push(event);
}

export function trackAddToCart(currencyCode: string, products: IGtmProductWithQuantity[]): void {
    const event: IGtmAddToCart = {
        event: "eec_addToCart",
        ecommerce: {
            currencyCode,
            add: {
                products,
            },
        },
    };

    push(event);
}

export function trackRemoveFromCart(currencyCode: string, products: IGtmProductWithQuantity[]): void {
    const event: IGtmRemoveFromCart = {
        event: "eec_removeFromCart",
        ecommerce: {
            currencyCode,
            add: {
                products,
            },
        },
    };

    push(event);
}

export function trackCheckoutOption(step: GtmCheckoutStep, option: string): void {
    const event: IGtmCheckoutOption = {
        event: "eec_checkoutOption",
        ecommerce: {
            'checkout_option': {
                actionField: {
                    step,
                    option,
                }
            },
        },
    };

    push(event);
}

export function trackPurchase(currencyCode: string, products: IGtmProductWithQuantity[], actionField: IGtmPurchaseActionField): void {
    const event: IGtmPurchase = {
        event: "eec_purchase",
        ecommerce: {
            currencyCode,
            purchase: {
                actionField,
                products
            },
        },
    };

    push(event);
}